import {
  collection,
  CollectionReference,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  getDocFromCache,
  getDocs,
  getDocsFromCache,
  limit,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  startAfter,
  where,
  WriteBatch,
  deleteField,
  writeBatch,
} from "firebase/firestore";
import { UserDocument } from "~/src/type/firebase/firestore";
import { AnalysisArticle, GraphInfo, HeadingAnalysisArticle } from "~/src/type/ui/article/article";
import { deleteAnalysisArticleImages } from "~/src/lib/firebase/storage";
import { firestore } from "../firebase/config";
import { isPostArticle, ReadPartQueryResult } from "./analysis";
import {
  parseFirestoreToAnalysisDocument,
  parseFirestoreToAnalysisDocumentList,
  parseFirestoreToGraphDocument,
  removeEmptyItemFromList,
  sortGraphInfoListByIndex,
} from "./common";
import {
  COLLECTION_DRAFT,
  COLLECTION_GRAPH,
  COLLECTION_USER,
  FIELD_DRAFT_UPDATE_DATE,
  FIELD_USER_ID,
} from "./info";

/**
 * グラフドキュメントID発行
 * @content user/{uid}/draft/{analysis-id}/graph/{graph-id} の graph-idを発行
 * @note 分析記事は一度下書きに保存されるため、
 *      （分析記事コレクションではなく）下書き側のサブコレクションでIDを発行する
 */
export function createNewGraphDocumentId(userId: string, analysisId: string): string {
  //------------
  // 自動発行ID
  //------------
  // コレクション
  // @note ますコレクションを作成する必要がある。doc()に直接コレクションが末尾のパス指定するとエラーになる
  // 『Invalid document reference. Document references must have an even number of segments』
  const graphCollection = collection(
    firestore,
    `${COLLECTION_USER}/${userId}/${COLLECTION_DRAFT}/${analysisId}/${COLLECTION_GRAPH}`
  );

  // 自動ID発行
  return doc(graphCollection).id;
}

/**
 * 下書き中の分析記事取得（グラフデータ含む）
 */
export async function readDraft(userId: string, id: string): Promise<AnalysisArticle | undefined> {
  //------------
  // 下書き取得
  //------------
  // 参照
  const docRef = doc(firestore, `${COLLECTION_USER}/${userId}/${COLLECTION_DRAFT}/${id}`);

  // ドキュメント取得
  const snapshot = await readDraftDocumentWithCache(docRef);
  if (!snapshot) {
    // なし
    return undefined;
  }

  //------------
  // グラフ取得
  //------------
  const graphList = await readDraftGraphDocumentWithCache(docRef);

  //------------
  // パース
  //------------
  // 分析記事のデータ構造にパース
  const analysisContents = parseFirestoreToAnalysisDocument(snapshot);

  return {
    id: id,
    contents: analysisContents,
    graph: graphList,
  };
}

/**
 * 下書きドキュメント取得（ドキュメントのみを取得する）
 *   1.   キャッシュから取得
 *   2-1. キャッシュから削除済み  → サーバから取得
 *   2-2. キャッシュなし         → サーバから取得
 */
async function readDraftDocumentWithCache(
  docRef: DocumentReference<DocumentData, DocumentData>
): Promise<DocumentSnapshot<DocumentData, DocumentData> | undefined> {
  console.log("readDraftDocumentWithCache() 開始");

  let docSnap;
  try {
    //------------
    // キャッシュから取得
    //------------
    docSnap = await getDocFromCache(docRef);
    if (docSnap.exists()) {
      return docSnap;
    }

    //------------
    // サーバから取得
    //------------
    docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap;
    }

    // データなし
    return undefined;
  } catch (e) {
    console.log("readDraftDocumentWithCache() キャッシュなしのため、サーバから取得する e:", e);

    //------------
    // サーバから取得
    //------------
    docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap;
    }

    // データなし
    return undefined;
  }
}

/**
 * 下書き記事のグラフを取得
 *   キャッシュから取得し、ない場合はfirestoreから取得する
 */
async function readDraftGraphDocumentWithCache(
  docRef: DocumentReference<DocumentData, DocumentData>
): Promise<GraphInfo[]> {
  console.log("readDraftGraphList() 開始");

  const graphCollection = collection(docRef, COLLECTION_GRAPH);
  let graphSnapshot;

  try {
    //------------
    // キャッシュから取得
    //------------
    graphSnapshot = await getDocsFromCache(graphCollection);
    if (graphSnapshot.empty) {
      //------------
      // キャッシュが空の場合、サーバから取得を試みる
      // （本番環境ではキャッシュが空でも例外が発生せず、empty: true を返すことがあるため）
      //------------
      graphSnapshot = await getDocs(graphCollection);
      if (graphSnapshot.empty) {
        return [];
      }
    }

    // 複数のグラフドキュメントを1件ずつパースする
    const graphInfoList: GraphInfo[] = graphSnapshot.docs.map((graphSnap) => {
      const id = graphSnap.id;
      const info = parseFirestoreToGraphDocument(graphSnap);

      return {
        id: id,
        info: info,
      };
    });

    // indexでソート
    return sortGraphInfoListByIndex(graphInfoList);
  } catch (e) {
    console.log("readDraftGraphList() キャッシュなしのため、サーバから取得する e:", e);

    graphSnapshot = await getDocs(graphCollection);
    if (graphSnapshot.empty) {
      return [];
    }

    // 複数のグラフドキュメントを1件ずつパースする
    const graphInfoList: GraphInfo[] = graphSnapshot.docs.map((graphSnap) => {
      const id = graphSnap.id;
      const info = parseFirestoreToGraphDocument(graphSnap);

      return {
        id: id,
        info: info,
      };
    });

    // indexでソート
    return sortGraphInfoListByIndex(graphInfoList);
  }
}

/**
 * 下書きリスト取得
 *   1.   サーバから取得（キャッシュも自動更新）
 *   2.   サーバ取得失敗時 → キャッシュから取得
 */
export async function readDraftList(
  readSize: number,
  userid: string,
  justBeforeArticle?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  console.log("readDraftList() 開始");

  //------------
  // クエリ
  //------------
  let q = query(
    collection(firestore, `${COLLECTION_USER}/${userid}/${COLLECTION_DRAFT}`),
    where(FIELD_USER_ID, "==", userid),
    orderBy(FIELD_DRAFT_UPDATE_DATE, "desc"),
    limit(readSize)
  );

  // 直前の読み込み記事があれば、その次から取得
  if (justBeforeArticle) {
    q = query(q, startAfter(justBeforeArticle));
  }

  try {
    //------------
    // サーバから取得（キャッシュも自動更新される）
    //------------
    return await readDraftListWithFirestore(q);
  } catch (e) {
    //------------
    // サーバ取得失敗時、キャッシュから取得
    //------------
    console.log("readDraftList() サーバ取得失敗のため、キャッシュから取得する e:", e);

    const ret = await readDraftListWithCache(q);
    if (ret) {
      return ret;
    }

    // キャッシュにもデータがない場合
    return {
      readLast: undefined,
      list: [],
    };
  }
}

/**
 * 下書きリスト取得（キャッシュからのみ取得）
 */
async function readDraftListWithCache(
  query: Query<DocumentData, DocumentData>
): Promise<ReadPartQueryResult | undefined> {
  //------------
  // キャッシュから取得
  //------------
  const querySnapshot = await getDocsFromCache(query);
  if (querySnapshot.empty) {
    // キャッシュ上のデータが空の場合
    return undefined;
  }

  //------------
  // 取得結果1件以上の場合
  //------------
  // パースして、取得結果を返す
  const draftList: HeadingAnalysisArticle[] = await parseFirestoreToAnalysisDocumentList(
    querySnapshot
  );

  return {
    readLast: querySnapshot.docs[querySnapshot.docs.length - 1],
    list: draftList,
  };
}

/**
 * 下書きリスト取得（サーバからのみ取得）
 */
async function readDraftListWithFirestore(
  query: Query<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  //------------
  // サーバから取得
  //------------
  const querySnapshot = await getDocs(query);
  if (querySnapshot.empty) {
    //------------
    // 取得結果0件の場合
    //------------
    return {
      readLast: undefined,
      list: [],
    };
  }

  //------------
  // 取得結果1件以上の場合
  //------------
  // パースして取得結果を返す
  const draftList: HeadingAnalysisArticle[] = await parseFirestoreToAnalysisDocumentList(
    querySnapshot
  );

  return {
    readLast: querySnapshot.docs[querySnapshot.docs.length - 1],
    list: draftList,
  };
}

/**
 * 下書き削除（単体）
 */
export async function removeDraft(userId: string, draftId: string, externBatch?: WriteBatch) {
  //------------
  // バッチ
  //------------
  // 呼び出し元から渡された場合、そのバッチで処理する
  const batch = externBatch ?? writeBatch(firestore);

  //------------
  // サブコレクション-下書き
  //------------
  const draftRef = doc(firestore, `${COLLECTION_USER}/${userId}/${COLLECTION_DRAFT}/${draftId}`);

  // サブコレクション "graph" の全ドキュメントを取得
  const graphCollection = collection(draftRef, COLLECTION_GRAPH);
  const graphSnap = await getDocs(graphCollection);

  //------------
  // グラフ削除
  //------------
  for (const doc of graphSnap.docs) {
    batch.delete(doc.ref);
  }

  //------------
  // 下書き記事削除
  //------------
  batch.delete(draftRef);

  //------------
  // コミット
  //------------
  if (!externBatch) {
    await batch.commit();

    //------------
    // 本文画像のクリーンアップ
    //------------
    // @note 呼び出し元からバッチを渡された場合（投稿処理・投稿記事削除処理の一部としての下書き削除）は、
    //       同じ記事IDを投稿記事側が引き続き参照している（または直後に参照する）ため、ここでは削除しない。
    //       スタンドアロン呼び出し（マイページからの下書き削除）の場合のみ、
    //       対応する投稿記事が存在しないことを確認した上で画像を削除する
    //       （投稿記事再編集中の下書きを削除しただけのケースでは、投稿記事側がまだ画像を参照しているため）。
    const stillPosted = await isPostArticle(draftId);
    if (stillPosted === false) {
      await deleteAnalysisArticleImages(userId, draftId).catch((error) => {
        console.error("[removeDraft] 画像クリーンアップエラー", error);
      });
    }
  }
}

/**
 * 下書き新規作成 or 更新
 */
export async function createUpdateDraft(userId: string, analysisArticle: AnalysisArticle) {
  const { id, contents } = analysisArticle;

  console.log("createUpdateDraft() userId: ", userId);
  console.log("createUpdateDraft() id: ", id);

  //------------
  // リストデータの空データ削除処理
  //------------
  // リスト構造のデータに関して、空のデータは保存しない
  removeEmptyItemFromList(contents);

  //------------
  // 記事内データ更新
  //------------
  // 現在の投稿ユーザー名
  const userRef = doc(firestore, COLLECTION_USER, userId);
  const userSnapShot = await getDoc(userRef);
  const userData = userSnapShot.data() as UserDocument;
  if (userData) {
    contents.userName = userData.name;
  }

  // 下書き編集日時
  contents.draftUpdatedAt = serverTimestamp();

  //------------
  // サブコレクション-下書き
  //------------
  // 下書き情報
  const draftCollection = collection(userRef, COLLECTION_DRAFT);
  const draftRef = doc(draftCollection, id);
  const draftDocSnap = await getDoc(draftRef);

  // 下書きがないかどうか（保存対象の分析がDBにない場合）
  if (!draftDocSnap.exists()) {
    //------------
    // 新規追加
    //------------
    await createDraft(draftCollection, analysisArticle);
  } else {
    //------------
    // 更新
    //------------
    await updateDraft(draftRef, analysisArticle);
  }
}

/**
 * 下書きの新規作成
 */
async function createDraft(
  draftCollection: CollectionReference<DocumentData, DocumentData>,
  analysisArticle: AnalysisArticle
) {
  const { id, contents, graph } = analysisArticle;

  const batch = writeBatch(firestore);

  //------------
  // 下書き
  //------------
  // 新規追加
  const draftDocRef = doc(draftCollection, id);
  batch.set(draftDocRef, contents);

  //------------
  // グラフ
  //------------
  console.log("■サブコレクション-グラフ 保存数 graphInfoList.length=" + graph.length);

  // グラフ情報をサブコレクションに追加
  const graphCollection = collection(draftDocRef, COLLECTION_GRAPH);
  for (const graphInfo of graph) {
    const graphDocRef = doc(graphCollection, graphInfo.id);
    batch.set(graphDocRef, graphInfo.info);
  }

  // コミット
  await batch.commit();
}

/**
 * 下書きの更新
 */
async function updateDraft(
  draftRef: DocumentReference<DocumentData, DocumentData>,
  analysisArticle: AnalysisArticle
) {
  const { contents, graph } = analysisArticle;

  const batch = writeBatch(firestore);

  //------------
  // 下書きの更新
  //------------
  if ("body" in contents && contents.body) {
    // 新形式：旧テキストフィールドをFirestoreから削除
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: Record<string, any> = {
      ...contents,
      details: deleteField(),
      premise: deleteField(),
      graphDescription: deleteField(),
      conclusion: deleteField(),
      reference: deleteField(),
    };
    batch.update(draftRef, updateData);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    batch.update(draftRef, contents as any);
  }

  //============
  // グラフ
  //============
  // グラフを取得
  const graphCollection = collection(draftRef, COLLECTION_GRAPH);
  const savedGraphList = await getDocs(graphCollection);

  //------------
  // グラフの更新・削除
  //------------
  // 更新完了リスト
  const updatedGraph: string[] = [];

  // まずは、DBに対して更新・削除から行う
  for (const docSnapshot of savedGraphList.docs) {
    // DB上のグラフが、更新対象にあるかどうか
    const existGraph = graph.find((info) => info.id === docSnapshot.id);
    if (existGraph) {
      //------------
      // 更新
      //------------
      const docRef = doc(graphCollection, docSnapshot.id);
      await batch.update(docRef, existGraph.info);

      //------------
      // 削除対象外
      //------------
      // 更新完了リストに追加
      updatedGraph.push(existGraph.id);

      continue;
    }

    //------------
    // 削除
    //------------
    // ない場合、firestoreから削除
    const docRef = doc(graphCollection, docSnapshot.id);

    await batch.delete(docRef);
  }

  //------------
  // グラフの新規追加
  //------------
  for (const graphInfo of graph) {
    const exist = updatedGraph.find((id) => id === graphInfo.id);

    // 更新済みなら処理なし
    if (exist) {
      continue;
    }

    // 新ドキュメントとして登録
    const ref = doc(graphCollection, graphInfo.id);
    batch.set(ref, graphInfo.info);
  }

  await batch.commit();
}
